import { ChevronLeft, ChevronRight } from "lucide-react";

import { createPaginationRange } from "@/ui";
import { tw } from "@/utils";

export interface PaginationProps {
  pageIndex: number;
  totalPages: number;
  siblingCount?: number;
  onPageChange: (pageIndex: number) => void;
  canPreviousPage: boolean;
  canNextPage: boolean;
}

export const Pagination = ({
  pageIndex,
  totalPages,
  siblingCount = 3,
  onPageChange,
  canPreviousPage,
  canNextPage,
}: PaginationProps) => {
  const paginationItems = createPaginationRange(
    pageIndex,
    totalPages,
    siblingCount,
  );

  return (
    <div className="flex items-center justify-self-end py-4">
      {pageIndex > 0 && (
        <button
          className="rounded px-4 py-2 disabled:opacity-50"
          onClick={() => onPageChange(pageIndex - 1)}
          disabled={!canPreviousPage}
        >
          <ChevronLeft size={16} />
        </button>
      )}
      <div className="flex items-center gap-2">
        {paginationItems.map((item, idx) => {
          if (item.type === "page" && item.pageNumber !== undefined) {
            const isActive = item.pageNumber === pageIndex + 1;
            return (
              <button
                key={item.pageNumber}
                className={tw(
                  "rounded px-3 py-1",
                  isActive
                    ? "bg-primary-50 text-primary-950"
                    : "text-neutral-500",
                )}
                onClick={() => onPageChange(item.pageNumber - 1)}
              >
                {item.pageNumber}
              </button>
            );
          }
          if (item.type === "ellipsis-left") {
            return (
              <button
                key={`ellipsis-left-${idx}`}
                className="px-3 py-1 text-neutral-500"
                onClick={() =>
                  onPageChange(Math.max(pageIndex - siblingCount, 0))
                }
              >
                ...
              </button>
            );
          }
          if (item.type === "ellipsis-right") {
            return (
              <button
                key={`ellipsis-right-${idx}`}
                className="px-3 py-1 text-neutral-500"
                onClick={() =>
                  onPageChange(
                    Math.min(pageIndex + siblingCount, totalPages - 1),
                  )
                }
              >
                ...
              </button>
            );
          }
          return null;
        })}
      </div>
      {pageIndex + 1 < totalPages && (
        <button
          className="rounded px-4 py-2 disabled:opacity-50"
          onClick={() => onPageChange(pageIndex + 1)}
          disabled={!canNextPage}
        >
          <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
};
